import React, { useState, useEffect } from 'react';
import { xeroAPI, emailSettingsAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Settings as SettingsIcon, Link2, Unlink, RefreshCw, Loader2, CheckCircle2, AlertCircle, Mail, Save, Plus, Trash2, Edit2, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('password');
  const [xeroStatus, setXeroStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Change password state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    api_key: '',
    sendgrid_api_key: '', // Legacy, kept for backward compatibility
    reply_to_email: '',
    reply_to_name: '',
    from_email: '',
    from_name: '',
    is_configured: false
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  
  // Template modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    is_default: false
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (isAdmin) {
        const [xeroRes, emailRes, templatesRes] = await Promise.all([
          xeroAPI.getStatus(),
          emailSettingsAPI.get(),
          emailSettingsAPI.getTemplates()
        ]);
        setXeroStatus(xeroRes.data);
        if (emailRes.data) {
          setEmailSettings(emailRes.data);
        }
        setEmailTemplates(templatesRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Change password handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordSuccess('Password changed successfully!');
      toast.success('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Xero functions
  const connectXero = async () => {
    try {
      const response = await xeroAPI.getAuthUrl();
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to initiate Xero connection');
    }
  };

  const disconnectXero = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Xero?')) return;
    try {
      await xeroAPI.disconnect();
      fetchData();
    } catch (error) {
      alert('Failed to disconnect from Xero');
    }
  };

  const syncClients = async () => {
    setSyncing(true);
    try {
      const response = await xeroAPI.syncClients();
      alert(`Synced ${response.data.synced} clients to Xero`);
    } catch (error) {
      alert('Failed to sync clients');
    } finally {
      setSyncing(false);
    }
  };

  const syncInvoices = async () => {
    setSyncing(true);
    try {
      const response = await xeroAPI.syncInvoices();
      alert(`Synced ${response.data.synced} invoices to Xero`);
    } catch (error) {
      alert('Failed to sync invoices');
    } finally {
      setSyncing(false);
    }
  };

  // Email settings functions
  const saveEmailSettings = async () => {
    setSavingEmail(true);
    setEmailError('');
    setEmailSuccess('');
    
    try {
      await emailSettingsAPI.update(emailSettings);
      setEmailSuccess('Email settings saved successfully');
      fetchData();
    } catch (error) {
      setEmailError(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSavingEmail(false);
    }
  };

  // Template functions
  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        subject: template.subject,
        body: template.body,
        is_default: template.is_default
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        subject: '',
        body: '',
        is_default: false
      });
    }
    setShowTemplateModal(true);
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    
    try {
      if (editingTemplate) {
        await emailSettingsAPI.updateTemplate(editingTemplate.id, templateForm);
      } else {
        await emailSettingsAPI.createTemplate(templateForm);
      }
      setShowTemplateModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await emailSettingsAPI.deleteTemplate(id);
      fetchData();
    } catch (error) {
      alert('Failed to delete template');
    }
  };

  if (loading && isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
          <p className="mt-1 text-slate-500">Manage your account and system settings</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('password')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'password'
                ? 'border-[#194eb2] text-[#194eb2]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="password-tab"
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Change Password
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab('email')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'email'
                    ? 'border-[#194eb2] text-[#194eb2]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Email Settings
              </button>
              <button
                onClick={() => setActiveTab('xero')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'xero'
                    ? 'border-[#194eb2] text-[#194eb2]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Xero Integration
              </button>
              <button
                onClick={() => setActiveTab('system')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'system'
                    ? 'border-[#194eb2] text-[#194eb2]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                System Info
              </button>
            </>
          )}
        </div>

        {/* Change Password Tab */}
        {activeTab === 'password' && (
          <div className="card-base">
            <div className="card-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#194eb2] rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Change Password</h2>
                  <p className="text-sm text-slate-500">Update your account password</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                {passwordError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-sm text-red-700">{passwordError}</span>
                  </div>
                )}
                
                {passwordSuccess && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-sm text-emerald-700">{passwordSuccess}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      required
                      className="input-base pr-10"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      required
                      minLength={6}
                      className="input-base pr-10"
                      placeholder="Enter new password (min 6 characters)"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                      className="input-base pr-10"
                      placeholder="Confirm new password"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="btn-primary flex items-center gap-2"
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Change Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email' && isAdmin && (
          <div className="space-y-6">
            {/* Resend Configuration */}
            <div className="card-base">
              <div className="card-header">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#194eb2] rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Email Configuration</h2>
                    <p className="text-sm text-slate-500">Configure Resend for sending PDF reports via email</p>
                  </div>
                </div>
              </div>
              <div className="card-body space-y-4">
                {emailError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{emailError}</p>
                  </div>
                )}
                {emailSuccess && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                    <p className="text-sm text-emerald-600">{emailSuccess}</p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Resend API Key
                  </label>
                  <input
                    type="password"
                    value={emailSettings.api_key || emailSettings.sendgrid_api_key || ''}
                    onChange={(e) => setEmailSettings({ ...emailSettings, api_key: e.target.value })}
                    className="input-base font-mono"
                    placeholder="re_xxxxxx..."
                    data-testid="resend-api-key"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#194eb2] hover:underline">Resend Dashboard</a>
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Reply-To Email
                    </label>
                    <input
                      type="email"
                      value={emailSettings.reply_to_email || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, reply_to_email: e.target.value })}
                      className="input-base"
                      placeholder="replies@yourcompany.com"
                    />
                    <p className="mt-1 text-xs text-slate-500">When clients reply, emails go here</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Reply-To Name
                    </label>
                    <input
                      type="text"
                      value={emailSettings.reply_to_name || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, reply_to_name: e.target.value })}
                      className="input-base"
                      placeholder="RHE Project Consulting"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      From Email
                    </label>
                    <input
                      type="email"
                      value={emailSettings.from_email || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                      className="input-base"
                      placeholder="timesheets@yourcompany.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={emailSettings.from_name || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                      className="input-base"
                      placeholder="RHE Project Consulting"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <button
                    onClick={saveEmailSettings}
                    disabled={savingEmail}
                    className="btn-primary flex items-center gap-2"
                    data-testid="save-email-settings"
                  >
                    {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Email Settings
                  </button>
                </div>
              </div>
            </div>

            {/* Email Templates */}
            <div className="card-base">
              <div className="card-header flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Email Templates</h2>
                  <p className="text-sm text-slate-500">Create templates for sending reports to clients</p>
                </div>
                <button
                  onClick={() => openTemplateModal()}
                  className="btn-primary flex items-center gap-2"
                  data-testid="add-template"
                >
                  <Plus className="w-4 h-4" />
                  Add Template
                </button>
              </div>
              <div className="card-body">
                {emailTemplates.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No templates yet. Create one to get started.</p>
                ) : (
                  <div className="space-y-3">
                    {emailTemplates.map((template) => (
                      <div key={template.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{template.name}</p>
                            {template.is_default && (
                              <span className="px-2 py-0.5 bg-[#194eb2] text-white text-xs rounded-full">Default</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{template.subject}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openTemplateModal(template)}
                            className="p-2 text-slate-500 hover:bg-slate-200 rounded-md"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTemplate(template.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Available Placeholders</h4>
                  <p className="text-sm text-blue-700">
                    Use these in your templates: <code className="bg-blue-100 px-1 rounded">{'{client_name}'}</code>, 
                    <code className="bg-blue-100 px-1 rounded ml-1">{'{month}'}</code>, 
                    <code className="bg-blue-100 px-1 rounded ml-1">{'{year}'}</code>, 
                    <code className="bg-blue-100 px-1 rounded ml-1">{'{resource_name}'}</code>, 
                    <code className="bg-blue-100 px-1 rounded ml-1">{'{period}'}</code>, 
                    <code className="bg-blue-100 px-1 rounded ml-1">{'{company_name}'}</code>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Xero Tab */}
        {activeTab === 'xero' && isAdmin && (
          <div className="card-base">
            <div className="card-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#13B5EA] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">X</span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Xero Integration</h2>
                  <p className="text-sm text-slate-500">Connect to Xero for accounting and invoicing</p>
                </div>
              </div>
            </div>
            <div className="card-body space-y-6">
              {/* Connection Status */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {xeroStatus?.is_connected ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium text-slate-900">
                      {xeroStatus?.is_connected ? 'Connected to Xero' : 'Not Connected'}
                    </p>
                    <p className="text-sm text-slate-500">{xeroStatus?.message}</p>
                  </div>
                </div>
                
                {xeroStatus?.is_configured ? (
                  xeroStatus?.is_connected ? (
                    <button
                      onClick={disconnectXero}
                      className="btn-secondary text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Unlink className="w-4 h-4" />
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={connectXero}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Link2 className="w-4 h-4" />
                      Connect to Xero
                    </button>
                  )
                ) : (
                  <span className="text-sm text-slate-500">API credentials not configured</span>
                )}
              </div>

              {/* Sync Options */}
              {xeroStatus?.is_connected && (
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900">Sync Options</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={syncClients}
                      disabled={syncing}
                      className="btn-secondary flex items-center justify-center gap-2 h-12"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sync Clients to Xero
                    </button>
                    <button
                      onClick={syncInvoices}
                      disabled={syncing}
                      className="btn-secondary flex items-center justify-center gap-2 h-12"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sync Invoices to Xero
                    </button>
                  </div>
                </div>
              )}

              {/* Setup Instructions */}
              {!xeroStatus?.is_configured && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Setup Required</h4>
                  <p className="text-sm text-amber-700 mb-3">
                    To connect Xero, you need to configure your Xero API credentials:
                  </p>
                  <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                    <li>Go to <a href="https://developer.xero.com" target="_blank" rel="noopener noreferrer" className="underline">developer.xero.com</a></li>
                    <li>Create or select your app</li>
                    <li>Copy your Client ID and Client Secret</li>
                    <li>Set XERO_CLIENT_ID and XERO_CLIENT_SECRET in your .env file</li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Info Tab */}
        {activeTab === 'system' && isAdmin && (
          <div className="card-base">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-slate-900">System Information</h2>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <dt className="text-sm font-medium text-slate-500">Application</dt>
                  <dd className="mt-1 text-lg font-semibold text-slate-900">RHE Timesheets</dd>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <dt className="text-sm font-medium text-slate-500">Version</dt>
                  <dd className="mt-1 text-lg font-semibold text-slate-900">1.0.0</dd>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <dt className="text-sm font-medium text-slate-500">Weekly Period</dt>
                  <dd className="mt-1 text-lg font-semibold text-slate-900">Sunday - Saturday</dd>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <dt className="text-sm font-medium text-slate-500">GST Rate</dt>
                  <dd className="mt-1 text-lg font-semibold text-slate-900">10%</dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowTemplateModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              {editingTemplate ? 'Edit Template' : 'Add Template'}
            </h3>
            
            <form onSubmit={saveTemplate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  required
                  className="input-base"
                  placeholder="Monthly Client Report"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Subject *
                </label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  required
                  className="input-base"
                  placeholder="Timesheet Summary for {month} {year}"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Body (HTML) *
                </label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  required
                  rows={10}
                  className="input-base font-mono text-sm"
                  placeholder="<p>Dear {client_name},</p><p>Please find attached...</p>"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={templateForm.is_default}
                  onChange={(e) => setTemplateForm({ ...templateForm, is_default: e.target.checked })}
                  className="h-4 w-4 text-[#194eb2] focus:ring-[#194eb2] border-slate-300 rounded"
                />
                <label htmlFor="is_default" className="text-sm text-slate-700">
                  Set as default template
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowTemplateModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingTemplate}
                  className="btn-primary"
                >
                  {savingTemplate ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SettingsPage;
